/**
 * Firebase Function Calls Utility
 *
 * Centralized utility for calling Firebase Functions with proper error handling
 */

import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebaseClient';
import { getFunctionName } from './functionMapping';

/**
 * Call HTTP Cloud Function endpoint with Firebase Auth
 * Used for V2 functions deployed via gcloud
 */
export const callHttpFunction = async (functionName: string, data: any = {}) => {
  try {
    const mappedFunctionName = getFunctionName(functionName);
    const functionUrl = `https://us-central1-evalpro2.cloudfunctions.net/${mappedFunctionName}`;

    console.log(`🌐 Calling HTTP function: ${functionName} -> ${mappedFunctionName}`, data);
    console.log(`🌐 Function URL: ${functionUrl}`);

    // Get current user's Firebase Auth token
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to call this function');
    }

    const token = await currentUser.getIdToken();

    // Make HTTP request with Firebase Auth token
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ HTTP ${response.status}: ${errorText}`);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ HTTP function ${functionName} result:`, result);
    return result;

  } catch (error) {
    console.error(`❌ Error calling HTTP function ${functionName}:`, error);
    throw error;
  }
};

/**
 * Generic function to call Firebase Functions with error handling
 */
export const callFirebaseFunction = async (functionName: string, data: any = {}) => {
  let mappedFunctionName: string = functionName;
  try {
    // Map camelCase to snake_case for Python backend
    mappedFunctionName = getFunctionName(functionName);

    console.log(`🔥 Calling Firebase function: ${functionName} -> ${mappedFunctionName}`, data);
    console.log(`🔥 Function URL would be: https://us-central1-evalpro2.cloudfunctions.net/${mappedFunctionName}`);

    const callable = httpsCallable(functions, mappedFunctionName);
    const result = await callable(data);

    console.log(`✅ Firebase function ${functionName} result:`, result.data);
    return result.data;
  } catch (error: any) {
    console.error(`❌ Error calling Firebase function ${functionName}:`, error);
    console.error(`❌ Error code:`, error.code);
    console.error(`❌ Error message:`, error.message);
    console.error(`❌ Error details:`, error.details);
    console.error(`❌ Full error object:`, JSON.stringify(error, null, 2));

    // Handle different error types
    if (error.code === 'functions/unavailable') {
      throw new Error('Service temporarily unavailable. Please try again later.');
    } else if (error.code === 'functions/deadline-exceeded') {
      throw new Error('Request timed out. Please try again.');
    } else if (error.code === 'functions/unauthenticated') {
      throw new Error('Authentication required. Please log in.');
    } else if (error.code === 'functions/permission-denied') {
      throw new Error('Permission denied. You do not have access to this feature.');
    } else if (error.code === 'functions/not-found') {
      throw new Error('Function not found. Please contact support.');
    } else if (error.code === 'functions/aborted') {
      throw new Error('Request was cancelled. Please try again.');
    } else if (error.code === 'functions/out-of-range') {
      throw new Error('Invalid input. Please check your data and try again.');
    } else if (error.code === 'functions/unimplemented') {
      throw new Error('Feature not implemented yet.');
    } else if (error.code === 'functions/internal') {
      throw new Error('Internal server error. Please try again later.');
    } else if (error.code === 'functions/data-loss') {
      throw new Error('Data corruption detected. Please contact support.');
    } else if (error.code === 'functions/failed-precondition') {
      throw new Error('Precondition failed. Please check your input.');
    } else if (error.code === 'functions/cancelled') {
      throw new Error('Operation was cancelled.');
    } else if (error.code === 'functions/unknown') {
      throw new Error('Unknown error occurred. Please try again.');
    } else if (error.code === 'functions/invalid-argument') {
      throw new Error('Invalid argument provided.');
    } else if (error.code === 'functions/already-exists') {
      throw new Error('Resource already exists.');
    } else if (error.code === 'functions/resource-exhausted') {
      throw new Error('Resource exhausted. Please try again later.');
    } else {
      // Generic error handling
      const errorMessage = error.message || 'An unexpected error occurred';
      throw new Error(errorMessage);
    }
  }
};

/**
 * Call updateGlobalSettings function
 */
export const updateGlobalSettings = async (settings: any) => {
  return await callFirebaseFunction('updateGlobalSettings', { settings });
};

/**
 * Call createCheckoutSession function
 */
export const createCheckoutSession = async (packageId: string, successUrl: string, cancelUrl: string, metadata: any = {}) => {
  return await callFirebaseFunction('createCheckoutSession', {
    packageId,
    successUrl,
    cancelUrl,
    metadata
  });
};

/**
 * Call retrieveCheckoutSession function
 */
export const retrieveCheckoutSession = async (sessionId: string) => {
  return await callFirebaseFunction('retrieveCheckoutSession', { sessionId });
};

/**
 * Call getPaymentHistory function
 */
export const getPaymentHistory = async (limit: number = 10) => {
  return await callFirebaseFunction('getPaymentHistory', { limit });
};

/**
 * Call provision_tenant function
 */
export const provisionTenant = async (organizationName: string, referralCode: string | null, userData: any) => {
  return await callFirebaseFunction('provisionTenant', {
    organizationName,
    referralCode,
    userData
  });
};

/**
 * Call updateTenantSettings function
 */
export const updateTenantSettings = async (tenantId: string, settings: any) => {
  return await callFirebaseFunction('updateTenantSettings', {
    tenantId,
    settings
  });
};

/**
 * Call getTenantSettings function
 */
export const getTenantSettings = async (tenantId: string) => {
  return await callFirebaseFunction('getTenantSettings', { tenantId });
};

/**
 * Call getGlobalSettings function
 */
export const getGlobalSettings = async (forceRefresh: boolean = false) => {
  return await callFirebaseFunction('getGlobalSettings', { forceRefresh });
};

/**
 * Call updateUserRole function
 */
export const updateUserRole = async (userId: string, role: string, tenantId: string | null = null) => {
  return await callFirebaseFunction('updateUserRole', {
    userId,
    role,
    tenantId
  });
};

/**
 * Call deleteUser function
 */
export const deleteUser = async (userId: string) => {
  return await callFirebaseFunction('deleteUser', { userId });
};

/**
 * Call getUsers function
 */
export const getUsers = async (tenantId: string | null = null, limit: number = 50) => {
  return await callFirebaseFunction('getUsers', { tenantId, limit });
};

/**
 * Call sendFeedback function
 */
export const sendFeedback = async (feedbackData: any) => {
  return await callFirebaseFunction('sendFeedback', feedbackData);
};

/**
 * Call respondToFeedback function
 */
export const respondToFeedback = async (feedbackId: string, response: string) => {
  return await callFirebaseFunction('respondToFeedback', {
    feedbackId,
    response
  });
};

/**
 * Call getFeedback function
 */
export const getFeedback = async (tenantId: string | null = null, limit: number = 50) => {
  return await callFirebaseFunction('getFeedback', { tenantId, limit });
};

/**
 * Call getAnalytics function
 */
export const getAnalytics = async (tenantId: string | null = null, dateRange: string = '30d') => {
  return await callFirebaseFunction('getAnalytics', { tenantId, dateRange });
};

/**
 * Call exportData function
 */
export const exportData = async (dataType: string, tenantId: string | null = null, format: string = 'json') => {
  return await callFirebaseFunction('exportData', {
    dataType,
    tenantId,
    format
  });
};

/**
 * Call importData function
 */
export const importData = async (data: any, dataType: string, tenantId: string | null = null) => {
  return await callFirebaseFunction('importData', {
    data,
    dataType,
    tenantId
  });
};

/**
 * Call generateApiKey function
 */
export const generateApiKey = async (tenantId: string | null = null) => {
  return await callFirebaseFunction('generateApiKey', { tenantId });
};

/**
 * Call revokeApiKey function
 */
export const revokeApiKey = async (apiKeyId: string) => {
  return await callFirebaseFunction('revokeApiKey', { apiKeyId });
};

/**
 * Call getApiKeys function
 */
export const getApiKeys = async (tenantId: string | null = null) => {
  return await callFirebaseFunction('getApiKeys', { tenantId });
};

export default {
  callFirebaseFunction,
  updateGlobalSettings,
  createCheckoutSession,
  retrieveCheckoutSession,
  getPaymentHistory,
  provisionTenant,
  updateTenantSettings,
  getTenantSettings,
  getGlobalSettings,
  updateUserRole,
  deleteUser,
  getUsers,
  sendFeedback,
  respondToFeedback,
  getFeedback,
  getAnalytics,
  exportData,
  importData,
  generateApiKey,
  revokeApiKey,
  getApiKeys
};